/**
 * @file 文件上传
 * @author svon.me@gmail.com
 * @description 前端直传 Aliyun OSS
 */

import {getSignature, UploadOrigin} from "./sign";
import {getAliOssClient, AliOssOption} from "./client";

import type {Signature} from "./sign";
import type {Checkpoint} from "ali-oss";

type ChangeCallback = (update: Upload) => void;

export enum Status {
  wait = "wait",         // 等待中 / 暂停中
  progress = "progress", // 进行中
  complete = "complete", // 上传完成
  abnormal = "abnormal", // 异常情况
}

export default class Upload {
  public size: number = 0;              // 文件大小 (字节)
  public name?: string;                 // 文件名称
  public progress: number = 0;          // 上传进度, 最大值 1
  public url?: string;                  // 文件链接
  protected file?: File;                // 文件
  public blockSize: number = 1;      // 大文件切片颗粒度大小, 默认 1M
  private event = new Set<ChangeCallback>();

  protected sign?: Signature;

  public status: Status = Status.wait; // 上传状态


  // 异常时上传次数
  public tryCount: number = 1;

  //记录上传进度
  private abortCheckpoint: Checkpoint | undefined;

  /**
   * @param data   自定义上传时文件外的数据信息
   * @param header 自定义 http header 附加信息
   */
  constructor(private data: object = {}, private header: object = {}) {
  }

  public onChange(progress: number = 0, url: string, checkpoint?: Checkpoint) {
    this.url = url;
    this.abortCheckpoint = checkpoint;

    if (url) {
      this.progress = progress;
      this.status = progress < 1 ? Status.progress : Status.complete;
    } else {
      this.status = Status.abnormal;
    }

    for (const callback of this.event) {
      callback(this);
    }
  }


  // 绑定回调事件
  on(callback: ChangeCallback) {
    if (callback) {
      this.event.add(callback);
    }
  }

  /**
   * @param file 文件对象
   * @description 直接上传文件
   */
  setFile(file: File) {
    this.file = file;
    this.name = file.name;
    this.size = file.size;
  }

  /**
   * @param base64   Base64
   * @param mimeType 图片类型
   * @name  name     图片名称
   * @description 将 base64 转换为图片并进行上传
   */
  setBase64(base64: string, mimeType = "image/png", name: string = "image.png") {
    const splitIndex = base64.indexOf(",");
    const byteString = atob(base64.slice(splitIndex + 1));     // base64 内容

    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], {type: mimeType || 'image/png'});
    const file = new File([blob], name || 'image.png', {type: mimeType || 'image/png'});
    return this.setFile(file);
  }

  /**
   * @param base64   Base64
   * @param mimeType 图片类型
   * @param quality  图片质量
   * @name  name     图片名称
   * @description 将 Canvas 转换为图片并进行上传
   */
  setCanvas(canvas: CanvasRenderingContext2D, mimeType = "image/png", quality = 1, name: string = "image") {
    try {
      // @ts-ignore
      const value = canvas.toDataURL(mimeType, quality);
      return this.setBase64(value, mimeType, name);
    } catch (error) {
      // todo
      console.log(error);
    }
  }

  /**
   * aliyun 大文件上传
   * @param checkpoint 上传进度
   */
  private multipart(checkpoint?: Checkpoint) {
    const signature: Signature = this.sign!;
    const client = getAliOssClient(signature);
    if (!signature || !client) {
      // 上传文件异常
      this.onChange(0, "");
      return;
    }
    const size = Math.ceil(this.file!.size / 1024 / 1024);
    // 设置分片大小 字节, 默认值为1 MB，最小值为100 KB
    let partSize = 1024 * 1024 * this.blockSize; // 1024 * 1024 * N
    if (size < 100) {
      // 文件大于 100M 则切片为 partSize 大小，小于 100M 则切片为 partSzie / 2 大小
      partSize = partSize / 2;
    }
    const options = {
      ...AliOssOption(signature.fileName),
      mime: this.file?.type,
      partSize,
      checkpoint,
      parallel: 4, // 设置并发上传的分片数量
      // 获取分片上传进度、断点和返回值。
      progress: (progress: number, checkpoint: Checkpoint) => {
        this.onChange(progress, signature.url!, checkpoint);
      },
    };
    return client.multipartUpload(signature.path!, this.file, options);
  }

  /**
   * aliyun 小文件上传
   */
  private async simple() {
    const signature = this.sign!;
    const client = getAliOssClient(signature);
    if (signature && client) {
      const options = {
        ...AliOssOption(signature.fileName),
        mime: this.file?.type,
      };
      try {
        await client.put(signature.path!, this.file, options);
        // 文件上传成功
        this.onChange(1, signature.url!);
        return true;
      } catch (error) {
        // todo
      }
    }
    // 上传文件异常
    this.onChange(0, "");
  }

  /*
   * 获取签名
   **/
  public getSignature() {
    return getSignature(this.file, this.header, this.data);
  }

  public async start() {
    if (!this.sign) {
      this.sign = await this.getSignature();
    }
    if (this.sign && this.sign.origin === UploadOrigin.Multipart) {
      return this.multipart(this.abortCheckpoint);
    }
    if (this.sign) {
      return this.simple();
    }
  }

  // 重新上传
  restart() {
    this.tryCount = 1;
    this.sign = void 0;
    return this.start();
  }

  // 重新尝试上传
  again() {
    this.tryCount += 1;
    return this.start();
  }
}